import { Component, Input, OnInit, ViewChild, HostListener } from '@angular/core';
import { IonPopover, ToastController } from '@ionic/angular';
import { Integration } from 'src/app/models/Integration';
import { Transaction } from 'src/app/models/Transaction';
import { Webhook } from 'src/app/models/Webhook';
import { IntegrationService } from 'src/app/providers/integration-service/integration-service';
import { PilotService } from 'src/app/providers/pilot-service/pilot-service';
import { ProjectService } from 'src/app/providers/project-service/project-service';
import { ShortcutService } from 'src/app/providers/shortcut-service/shortcut-service';
import { TransactionService } from 'src/app/providers/transaction-service/transaction-service';
import { TranslationBridge } from 'src/app/providers/translation-bridge/translation-bridge';
import { WebhookService } from 'src/app/providers/webhook-service/webhook-service';

const AVAILABLE_FIELDS = [
  'amount',
  'purpose',
  'bankBookingDate',
  'counterpartName',
  'counterpartIban',
  'category',
];

@Component({
  selector: 'transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.scss'],
})
export class TransactionListComponent  implements OnInit {


  @HostListener('window:mouseup')
  stopRowSelection() {
    this.isMarking = false;
  }

  @ViewChild('popover') popover: IonPopover;
  isContextMenuOpen: boolean = false;

  contextMenuType: 'cell' | 'header' | 'selection' = 'cell';
  contextMenuTransaction: Transaction = null;
  contextMenuField: string = '';

  _transactions: Transaction[] = [];
  @Input() set transactions(transactions: Transaction[]) {
    this._transactions = transactions;
  }
  get transactions() {
    return this._transactions;
  }

  @Input() enableFilterBar: boolean = false;
  _filter: string = '';
  @Input() set filter(filter: string) {
    this._filter = filter;
  }
  get filter() {
    return this._filter;
  }
  
  _fields: string[] = [];
  @Input() set fields(fields: string[]) {
    this._fields = fields;
  }
  get fields() {
    return this._fields;
  }

  private isMarking = false;
  private markedStart: any = null;
  _selectedTransactions: Transaction[] = [];
  @Input() set selectedTransactions(selectedTransactions: Transaction[]) {
    this._selectedTransactions = selectedTransactions;
  }
  get selectedTransactions() {
    return this._selectedTransactions;
  }

  _categoryOptions = [];

  _fieldWidths: {[key: string]: string} = {};
  _allFieldWidthsSum: number = 800;

  private resizing:boolean = false;
  private currentField: string = '';
  private mouseDownX: number = 0;
  private resizingCellStartWidth: number = 0;


  constructor(
    private translate: TranslationBridge,
    private toastController: ToastController,
    private transactionService: TransactionService,
    private projectService: ProjectService,
    private pilotService: PilotService,
    public integrationService: IntegrationService,
    public webhookService: WebhookService
    ) { }

  ngOnInit() {

    if(!this.fields || this.fields.length === 0) {
      this.fields = [
        'amount',
        'purpose',
        'bankBookingDate',
        'counterpartName',
        'counterpartIban',
        'category',
      ];
    }

    // default field widths
    this._fieldWidths = AVAILABLE_FIELDS.reduce((acc, field) => {
      acc[field] = this.getFieldWidth(field);
      return acc;
    }, {});
    this.recalculateAllFieldWidthsSum();
  }

  ngAfterViewInit() {
  
    let projectId = this.projectService.getCurrentProjectId();
    this.transactionService.getCategoriesAsOptions(projectId).then((categories) => {
      this._categoryOptions = categories;
    });
  }

  getFieldTranslation(field: string) {
    const key = field.replace(/([A-Z])/g, '_$1').toUpperCase();
    return this.translate.get(`TRANSACTION_FIELD_${key}`);
  }

  getFieldWidth(field: string) {
    if(field === 'amount') {
      return '100px';
    }
    if(field === 'valueDate') {
      return '160px';
    }
    if(field === 'counterpartName') {
      return '220px';
    }
    if(field === 'counterpartIban') {
      return '220px';
    }
    if(field === 'category') {
      return '180px';
    }
    if(field === 'purpose') {
      return '360px';
    }
    return '220px';
  }

  recalculateAllFieldWidthsSum() {

    // calculate the sum of all field widths but only of the visible fields (.fields)
    this._allFieldWidthsSum = Object.keys(this._fieldWidths).reduce((acc, field) => {
      if(this.fields.includes(field)) {
        acc += parseInt(this._fieldWidths[field])+20; // +20 for the padding
      }
      return acc;
    }, 0);
  }

  startColumnResize(event: MouseEvent, field: string) {
    this.resizing = true;
    this.currentField = field;
    this.mouseDownX = event.pageX;
    this.resizingCellStartWidth = parseInt(this._fieldWidths[field]);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.stopColumnResize);
  }

  private onMouseMove = (event: MouseEvent) => {
    if (this.resizing) {
      const newWidth = event.pageX - this.mouseDownX + this.resizingCellStartWidth;
      
      if(newWidth < 20 || newWidth > 680) {
        return;
      }
      
      this._fieldWidths[this.currentField] = `${newWidth}px`;
      this.recalculateAllFieldWidthsSum();
    }
  }

  stopColumnResize = () => {
    this.resizing = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.stopColumnResize);
  }

  onContextHeaderClick(event: MouseEvent, field: string) {
    event.preventDefault();
    this.isContextMenuOpen = true;
    this.contextMenuTransaction = null;
    this.contextMenuType = 'header';
    this.contextMenuField = field;
    this.popover.event = event;
  }


  onContextClick(event: MouseEvent, transaction: any, field: string) {
    event.preventDefault();
    this.isContextMenuOpen = true;

    this.contextMenuTransaction = transaction;
    this.contextMenuField = field;
    this.contextMenuType = 'cell';
    this.popover.event = event;

    if(this._selectedTransactions.length === 1 && this._selectedTransactions.includes(transaction)) {
      this.contextMenuType = 'cell';
    } else if(this._selectedTransactions.length > 1 && this._selectedTransactions.includes(transaction)) {
      this.contextMenuType = 'selection';
      this.contextMenuTransaction = null;
    } else if(this._selectedTransactions.length > 1 && !this._selectedTransactions.includes(transaction)) {
      this.contextMenuType = 'cell';
      this.contextMenuTransaction = transaction;
    }

  }

  copyCell(field: string, transaction: Transaction) {
    this.isContextMenuOpen = false;
    this.copyValue(transaction[field]);
  }

  copyTransaction(transaction: Transaction, returnValue: boolean = false) {
    
    this.isContextMenuOpen = false;

    const flatTransaction = this.transactionService.flattenTransaction(transaction);

    // filter out the fields that are not in the fields array
    const filteredTransaction = Object.keys(flatTransaction).filter(field => this.fields.includes(field)).reduce((acc, field) => {
      acc[field] = flatTransaction[field];
      return acc;
    }, {});

    // also keep the order of the fields
    const orderedTransaction = this.fields.reduce((acc, field) => {
      acc[field] = filteredTransaction[field];
      return acc;
    }, {});


    const excelData = Object.values(orderedTransaction).map(value => value.toString()).join("\t");
    if(returnValue) {
      return excelData;
    }
    this.copyValue(excelData);
  }

  copySelectedTransactions(event: MouseEvent) {
    event.preventDefault();
    this.isContextMenuOpen = false;
    let excelData = '';
    for(const transaction of this._selectedTransactions) {
      excelData += this.copyTransaction(transaction, true);
      excelData += '\n';
    }
    this.copyValue(excelData);
  }

  copyHeader() {
    this.isContextMenuOpen = false;
    // copy just the fields without the values
    const header = this.fields.map(field => this.getFieldTranslation(field)).join("\t");
    this.copyValue(header);
  }

  copyValue(value: any){
    navigator.clipboard.writeText(value).then(() => {
      this.toastController.create({
        message: 'Copied to clipboard!',
        duration: 2000,
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    }).catch((err) => {
      this.toastController.create({
        message: 'Couldn\'t copy to clipboard.',
        duration: 2000,
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    });
  }

  /*
    Selection and Marking
  */
  onSelectTransaction(transaction: Transaction) {
    if(this._selectedTransactions.includes(transaction)) {
      this._selectedTransactions = this._selectedTransactions.filter(t => t !== transaction);
    } else {
      this._selectedTransactions = [...this._selectedTransactions, transaction];
    }
  }

  onSelectAllTransactions() {
    this._selectedTransactions = this.transactions;
  }

  onDeselectAllTransactions(event: MouseEvent) {
    event.stopPropagation(); // Prevent event bubbling
    event.preventDefault(); // Prevent default right-click behavior
    this._selectedTransactions = [];
    this.contextMenuTransaction = null;
    this.isContextMenuOpen = false; // Close the context menu
  }

  startRowSelection(event: MouseEvent,transaction: any) {
    // Only start selection on left mouse button (button 0)
    if (event.button !== 0) return;
    
    this.isMarking = true;
    this.markedStart = transaction;
    this.updateRowSelection(transaction);
  }

  updateRowSelection(transaction: any) {
    if (!this.isMarking) return;

    const startIndex = this.transactions.indexOf(this.markedStart);
    const endIndex = this.transactions.indexOf(transaction);
    const [min, max] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];

    this._selectedTransactions = this.transactions.slice(min, max + 1);
  }

  isTransactionSelected(transaction: any): boolean {
    return this._selectedTransactions.includes(transaction);
  }


  /*
    Sending to Webhooks
  */
  sendSelectedTransactionsToWebhook(webhook: Webhook) {
    this.isContextMenuOpen = false;
    this.sendTransactionsToWebhook(webhook, this._selectedTransactions);
  }

  sendClickedTransactionsToWebhook(webhook: Webhook) {
    this.isContextMenuOpen = false;
    this.sendTransactionsToWebhook(webhook, [this.contextMenuTransaction]);
  }

  /*
    Sending to Integrations
  */
  sendSelectedTransactionsToIntegration(integration: Integration) {
    this.isContextMenuOpen = false;
    this.sendTransactionsToIntegration(integration, this._selectedTransactions);
  }

  sendClickedTransactionsToIntegration(integration: Integration) {
    this.isContextMenuOpen = false;
    this.sendTransactionsToIntegration(integration, [this.contextMenuTransaction]);
  }


  private sendTransactionsToIntegration(integration: Integration, transactions: Transaction[]) {
    var ids = [];
    for(const transaction of transactions) {
      if(transaction._id){
        ids.push(transaction._id.toString());
        // @ts-ignore
      } else if(transaction.id) {
        // @ts-ignore
        ids.push(transaction.id.toString());
      } else {
        console.error('No transaction id found');
      }
    }

    this.integrationService.sendTransactionsToIntegration(integration.id, ids).then((response) => {
      this.toastController.create({
        message: this.translate.get('TRANSACTION_LIST_SEND_TO_INTEGRATION_SUCCESS').replace('{integration}', integration.name),
        duration: 2000,
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    }).catch((error) => {
      this.toastController.create({
        message: this.translate.get('TRANSACTION_LIST_SEND_TO_INTEGRATION_ERROR').replace('{integration}', integration.name),
        duration: 2000,
        cssClass: 'toast-error',
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    });

  }

  private sendTransactionsToWebhook(webhook: Webhook, transactions: Transaction[]) {
   
    var ids = [];
    for(const transaction of transactions) {
      if(transaction._id){
        ids.push(transaction._id.toString());
        // @ts-ignore
      } else if(transaction.id) {
        // @ts-ignore
        ids.push(transaction.id.toString());
      } else {
        console.error('No transaction id found');
      }
    }

    console.log(transactions, ids, webhook);

    this.webhookService.sendTransactionsToWebhook(webhook.id, ids).then((response) => {
      this.toastController.create({
        message: this.translate.get('TRANSACTION_LIST_SEND_TO_WEBHOOK_SUCCESS').replace('{webhook}', webhook.name),
        duration: 2000,
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    }).catch((error) => {
      this.toastController.create({
        message: this.translate.get('TRANSACTION_LIST_SEND_TO_WEBHOOK_ERROR').replace('{webhook}', webhook.name),
        duration: 2000,
        cssClass: 'toast-error',
        position: 'top'
      }).then((toast) => {
        toast.present();
      });
    });
  }

  /*
    AI Services
  */

    askCopilot(event: any,transaction: Transaction) {
      this.isContextMenuOpen = false;
      this.pilotService.setReferenceTransaction(transaction);
      this.pilotService.openMiniPilot(event);
    }
}
